import { useCallback, useMemo, useState } from 'react';
import { type SupportedCountryCode } from '@zep-us/i18n';
import { useTranslation } from 'next-i18next';

import { type CategoryTree } from '@/shared/category/type';
import { type CategoryInfoInputType } from '@/views/build/features/category-info';
import {
  generateGradeLabel,
  generateSchoolTypeLabel,
  generateSemesterLabel,
} from '@/views/build/features/category-reader';
import { type CategorySelectorState } from '@/views/build/features/category-select-view';
import { NONE_VALUE } from '@/views/build/features/category-tree';

import { type DisplayTextItems } from '../types';

/**
 * 카테고리 표시 텍스트 관리 hook
 *
 * 선택된 값과 호버 시 임시로 보여줄 값을 병합하여
 * 카테고리 표시 텍스트를 생성하는 독립적인 hook입니다.
 *
 * 책임:
 * - 호버 프리뷰 상태 관리 (useState)
 * - 선택된 값 + 호버 프리뷰 병합 (useMemo)
 * - 최종 표시 텍스트 생성 (generatedDisplayText)
 *
 * 개선사항:
 * - useState + useEffect 제거 → useMemo로 파생 상태 관리
 * - 순환 의존성 문제 해결
 */
export const useCategoryDisplayText = ({
  userCountryCode,
  categoryInfo,
  schoolInfoTree,
}: {
  /** 사용자 국가 코드 (제목 생성 시 필요) */
  userCountryCode: SupportedCountryCode;
  categoryInfo: CategorySelectorState;
  /** 학교 정보 트리 */
  schoolInfoTree: CategoryTree;
}) => {
  const { t } = useTranslation(['common']);

  // 호버 프리뷰 상태만 관리 (실제 사용자 인터랙션)
  const [hoverPreviewState, setHoverPreviewState] = useState<
    Partial<
      Record<keyof CategoryInfoInputType, { value: string; label: string }>
    >
  >({});

  // 라벨 생성 함수 - 동적 인자로 pure function 호출
  const generateDisplayLabel = useCallback(
    (
      field: keyof CategoryInfoInputType,
      {
        value,
        label,
      }: {
        value?: string;
        label?: string;
      },
    ) => {
      // 'none' 값은 표시하지 않음 (선택 안함)
      if (value === NONE_VALUE) {
        return;
      }

      // Pure functions를 사용하여 동적으로 label 생성
      switch (field) {
        case 'schoolTypeId': {
          // schoolInfoTree에서 school node 찾기
          const schoolTypeNode = schoolInfoTree.children.find(
            node => node.categoryGroupId === value,
          );
          if (!schoolTypeNode) return undefined;
          return generateSchoolTypeLabel(schoolTypeNode.name, t);
        }
        case 'gradeId':
          return generateGradeLabel(value, userCountryCode, t);
        case 'semesterId':
          return generateSemesterLabel(value, userCountryCode, t);

        case 'subject':
        case 'subSubject':
        case 'publisher':
        case 'customPublisher':
        case 'isLastSchoolInfoSaved':
        default:
          return `${label}`;
      }
    },
    [schoolInfoTree, userCountryCode, t],
  );

  // 표시 텍스트 항목들을 useMemo로 파생 (baseLabels 활용)
  const displayTextItems = useMemo((): DisplayTextItems => {
    const result: DisplayTextItems = {};

    // baseLabels를 기반으로 displayTextItems 구성
    const labelMapping = {
      schoolTypeId: {
        value: categoryInfo.schoolType.selectedSchoolTypeNode?.groupId,
        label: generateDisplayLabel('schoolTypeId', {
          value: categoryInfo.schoolType.selectedSchoolTypeNode?.groupId,
        }),
      },
      gradeId: {
        value: categoryInfo.grade.selectedGradeNode?.nodeId,
        label: generateDisplayLabel('gradeId', {
          value: categoryInfo.grade.selectedGradeNode?.nodeId,
        }),
      },
      semesterId: {
        value: categoryInfo.semester.selectedSemesterNode?.nodeId,
        label: generateDisplayLabel('semesterId', {
          value: categoryInfo.semester.selectedSemesterNode?.nodeId,
        }),
      },
      subject: {
        value: categoryInfo.subject.selectedSubjectNode?.nodeId,
        label: generateDisplayLabel('subject', {
          label: categoryInfo.subject.selectedSubjectNode?.label,
        }),
      },
      subSubject: {
        value: categoryInfo.subject.selectedSubSubjectNode?.nodeId,
        label: generateDisplayLabel('subSubject', {
          label: categoryInfo.subject.selectedSubSubjectNode?.label,
        }), // subSubject이 있으면 subjectLabel이 subSubject을 가리킴
      },
      publisher: {
        value: categoryInfo.publisher.selectedPublisher?.nodeId,
        label: generateDisplayLabel('publisher', {
          label: categoryInfo.publisher.selectedPublisher?.label,
        }),
      },
      customPublisher: {
        value: categoryInfo.publisher.selectedCustomPublisher,
        label: generateDisplayLabel('customPublisher', {
          label: categoryInfo.publisher.selectedCustomPublisher,
        }),
      },
    };

    Object.entries(labelMapping).forEach(([key, { value, label }]) => {
      const field = key as keyof CategoryInfoInputType;

      // 선택된 값이 있으면 baseLabels 사용
      if (value && value !== NONE_VALUE && label) {
        result[field] = {
          value,
          label,
          isHoverPreview: false,
        };
      }
      // 선택된 값 없고 호버 프리뷰가 있으면 프리뷰 사용
      else if (hoverPreviewState[field]) {
        result[field] = {
          value: hoverPreviewState[field]!.value,
          label: hoverPreviewState[field]!.label,
          isHoverPreview: true,
        };
      }
    });

    return result;
  }, [
    // FIX: categoryInfo 전체 객체 대신 실제 사용하는 속성만 의존성에 추가
    // 이렇게 하면 무관한 필드 변경 시 불필요한 재계산 방지
    categoryInfo.schoolType.selectedSchoolTypeNode,
    categoryInfo.grade.selectedGradeNode,
    categoryInfo.semester.selectedSemesterNode,
    categoryInfo.subject.selectedSubjectNode,
    categoryInfo.subject.selectedSubSubjectNode,
    categoryInfo.publisher.selectedPublisher,
    categoryInfo.publisher.selectedCustomPublisher,
    hoverPreviewState,
    generateDisplayLabel,
  ]);

  const updateHoverPreview = useCallback(
    ({
      field,
      value,
      label,
    }: {
      field: keyof CategoryInfoInputType;
      value: string;
      label?: string;
    }) => {
      // NONE_VALUE에 호버하면 프리뷰 생성 안 함
      if (value === NONE_VALUE) {
        return;
      }

      // FIX: Stale closure 방지 - displayTextItems 대신 categoryInfo에서 직접 확인
      // setState 내부에서 closure 변수를 읽지 않고, 외부에서 현재 상태 확인
      const getCurrentValue = () => {
        switch (field) {
          case 'schoolTypeId':
            return categoryInfo.schoolType.selectedSchoolTypeNode?.groupId;
          case 'gradeId':
            return categoryInfo.grade.selectedGradeNode?.nodeId;
          case 'semesterId':
            return categoryInfo.semester.selectedSemesterNode?.nodeId;
          case 'subject':
            return categoryInfo.subject.selectedSubjectNode?.nodeId;
          case 'subSubject':
            return categoryInfo.subject.selectedSubSubjectNode?.nodeId;
          case 'publisher':
            return categoryInfo.publisher.selectedPublisher?.nodeId;
          case 'customPublisher':
            return categoryInfo.publisher.selectedCustomPublisher;
          case 'isLastSchoolInfoSaved':
          default:
            return undefined;
        }
      };

      const currentValue = getCurrentValue();

      // 이미 선택된 값이 있고, 그 값이 NONE_VALUE가 아니면 프리뷰 업데이트 안 함
      if (currentValue && currentValue !== NONE_VALUE) {
        return;
      }

      setHoverPreviewState(prev => ({
        ...prev,
        [field]: {
          value,
          label: generateDisplayLabel(field, { value, label }),
        },
      }));
    },
    [
      // FIX: categoryInfo 전체 객체 대신 실제 사용하는 속성만 의존성에 추가
      // getCurrentValue()에서 읽는 필드들만 포함하여 불필요한 재생성 방지
      categoryInfo.schoolType.selectedSchoolTypeNode,
      categoryInfo.grade.selectedGradeNode,
      categoryInfo.semester.selectedSemesterNode,
      categoryInfo.subject.selectedSubjectNode,
      categoryInfo.subject.selectedSubSubjectNode,
      categoryInfo.publisher.selectedPublisher,
      categoryInfo.publisher.selectedCustomPublisher,
      generateDisplayLabel,
    ],
  );

  const clearHoverPreview = useCallback(
    (field: keyof CategoryInfoInputType) => {
      setHoverPreviewState(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    },
    [],
  );

  return {
    updateHoverPreview,
    clearHoverPreview,
    displayTextItems,
  };
};

export type CategoryDisplayText = ReturnType<typeof useCategoryDisplayText>;
